/*
  <spin-360-viewer> — a dependency-free 360° spin viewer for the Dawn theme.

  Markup (produced by snippets/product-spin-360.liquid):

    <spin-360-viewer>
      <script type="application/json" class="spin-360__config">
        {
          "activeVariantId": 123,
          "variants": { "123": "https://render.beyondcarat.com/renders/SKU/Shape/Color/ct-1/", ... },
          "spinVersion": "v2", "sizedFrames": 180, "sizedWidths": [640, 820],
          "frames": 360, "ext": "webp", "pad": 5, "framePrefix": "360/frame_",
          "autoSpin": true, "reverse": false, "dragSensitivity": 1
        }
      </script>
      (Carat: each variant base goes through window.BCCaratRenders.swap — see
      assets/carat-renders.js — so the spin follows the carat picked in the customizer.)
      <div class="spin-360">
        <canvas class="spin-360__canvas"></canvas>
        <img class="spin-360__poster" ...>      // shown until ready / no-JS fallback
        <div class="spin-360__loader"><div class="spin-360__spinner"></div></div>
        <div class="spin-360__badge">…360°…</div>
      </div>
    </spin-360-viewer>

  TWO frame schemes, chosen per ring by a one-image probe (no manifest, no CORS):

   - v2 "sized" (the default): {base}360/v2/{640|820}/frame_00001..frame_00180.webp —
     180 frames = one full turn at 2°/frame, ~8 KB each. Probe = an <img> load of
     frame_00001 at the picked width; success → sized mode, 180 frames.
   - native fallback (rings without v2): {base}360/frame_00001..frame_003NN.webp, the
     ~1080px originals — EXACTLY today's URLs and today's ladder/memory behaviour.

  Source sets come in TWO lengths: 360 frames (1°/frame, camera-orbit renders) and 180 frames
  (2°/frame, model-spin renders since 2026-08-28). Both ship the same 180-frame v2 tiers, so
  only the paths that touch the SOURCE frames (full-quality width, native fallback, hole
  fallback, thumbnail rocking in full mode) need to know which — a second one-image probe
  (native frame 181 exists only in a 360 set) decides that per ring, cached per base URL.

  The frame count is therefore a property of the CACHE (`cache.frames`: 180 sized /
  360 native), not of the element — wrapping, drawing, drag mapping, rungs, baseline
  speed and flick caps all read it.

  Behaviour:
   - Lazy: nothing loads until the viewer scrolls into view (IntersectionObserver),
     plus an idle warm-start that fetches rung 1 and pauses.
   - Progressive start everywhere: phones 16 → 32 → 64 → 128 → full, desktop 32 → 64 →
     128 → full. The first rung (evenly distributed frames) reveals and auto-spins
     immediately; every later rung fills the largest remaining angular gaps, so the same live
     spin gets smoother without resetting position. Nothing waits for the complete set.
   - Memory: sized 640 on phones = 180 × 640² × 4 ≈ 295 MB < 340 MB budget; 820 on
     desktop ≈ 484 MB < 700 MB — the full v2 set always fits, so the gate never stops a
     v2 ring short. Native (1080²) still caps out early, as today.
   - Every frame is decoded (img.decode()) before it counts as loaded, so a draw never
     stalls on a synchronous decode.
   - Drag (mouse) + swipe (touch via Pointer Events) + inertia + auto-spin. Motion is
     time-based: baseline = frames / 12 s per turn; a flick decays toward that baseline
     with a 4 s time-constant PLUS constant friction (0.044 turns/s²) and eases back in
     with a 0.8 s constant if it drops below — so a glide melts into the auto-spin with
     no pause and never re-accelerates past it.
   - Per-variant: swaps the active spin on Dawn's `variant-change` pub/sub event,
     caching each variant's frames independently.

  Position is tracked as a float `pos` (frames). The integer frame to draw is only
  derived (rounded) at draw time, so fractional auto-spin/inertia accumulate smoothly.
*/

(function () {
  'use strict';

  if (customElements.get('spin-360-viewer')) return;

  var CONCURRENCY = 8; // parallel image requests (fine over HTTP/2-3 multiplexing)

  // Pre-uploaded WebP renditions (widths) of the v2 scheme. The client picks the smallest
  // >= the displayed size (× DPR, capped) — see _pickWidth. We encode these ourselves (no
  // on-the-fly AVIF), which keeps decode cheap on weak phones. v2 ships only 640 + 820:
  // 820 is already >= any phone/desktop display box we render the spin in, and both fit the
  // memory budget with all 180 frames resident. Frames: {base}360/{version}/{width}/.
  var SIZES = [640, 820, 960]; // 960 exists on newer sets only — the probe steps down per ring
  // "Full quality" = the untouched ~1080px source frames, served AS the top rung of the sized
  // scheme (sized frame k = source frame 2k-1, so no new files are needed and every ring has
  // them). Offered only to big desktops whose browser reports plenty of memory — the full ring
  // is 180 × 1080² × 4 ≈ 838 MB decoded.
  var FULL_WIDTH = 1080;
  var SIZED_FRAMES = 180; // v2: one turn at 2°/frame (the odd source frames 1..359)
  var NATIVE_FRAMES = 360; // fallback: the original 1°/frame set (older renders)
  var NATIVE_FRAMES_SHORT = 180; // model-spin renders: 180 source frames at 2°/frame
  // Native frame count per ring, decided by loading native frame 181 (present only in a 360
  // set): onload → 360, onerror → 180. One request, only on paths that read the source frames.
  var NATIVE_PROBE_FRAME = 181;
  var _nativeCounts = {}; // base -> 180 | 360 once probed
  var _nativeWaiters = {}; // base -> callbacks waiting on an in-flight probe
  function nativeFramesFor(base, fallback) {
    return _nativeCounts[base] || fallback || NATIVE_FRAMES;
  }
  function probeNativeFrames(base, probeUrl, longCount, cb) {
    if (_nativeCounts[base]) { cb(_nativeCounts[base]); return; }
    if (_nativeWaiters[base]) { _nativeWaiters[base].push(cb); return; }
    _nativeWaiters[base] = [cb];
    var settle = function (n) {
      _nativeCounts[base] = n;
      var waiters = _nativeWaiters[base] || [];
      delete _nativeWaiters[base];
      for (var i = 0; i < waiters.length; i++) waiters[i](n);
    };
    var img = new Image();
    img.decoding = 'async';
    try { img.fetchPriority = 'low'; } catch (e) {}
    img.onload = function () { settle(longCount || NATIVE_FRAMES); };
    img.onerror = function () { settle(NATIVE_FRAMES_SHORT); };
    img.src = probeUrl;
  }
  // Cumulative frame ladders (clamped to the ring's frame count). Every mode starts small and
  // refines in place: the first rung reveals the canvas AND starts the auto-spin, later rungs
  // fill the largest remaining angular gaps. Power-of-two stops keep each prefix of the
  // largest-gap load order evenly spread, so the spin steps uniformly at every stage.
  //   phones  16 → 32 → 64 → 128 → full (unchanged)
  //   desktop 32 → 64 → 128 → full — replaces sized 45 → 90 → 180 (which revealed at 90 and
  //           only auto-spun once all 180 were in) and native 30 → 60 → 120 → 180 → 360.
  var RUNG_TARGETS_MOBILE = [16, 32, 64, 128, 256, 360];
  var RUNG_TARGETS_DESKTOP = [32, 64, 128, 256, 360];
  var FPS_OK = 48; // climb to the next rung only while the spin holds ~this frame rate
  var FPS_RETRY_MS = 3000; // a failed FPS check waits this long and re-tests…
  var FPS_RETRIES = 3; // …up to this many times before the ladder gives up for good
  var FPS_STALE_MS = 1000; // an FPS sample older than this (loop idle) is no evidence at all
  var MIN_RESIDENT = 60; // the coarse set is always allowed to load, whatever the budget says
  var THUMB_CONCURRENCY = 4; // thumbnail rocking-frame preloads in flight at once (was: all ~30)
  // Measured 1:1 from brilliance.com's 360 viewer. All motion is TIME-based (seconds), so it
  // runs identically on 60Hz and 120Hz/ProMotion displays (was 2× too fast on 120Hz).
  var AUTO_PERIOD_SEC = 12; // 12s per full rotation steady auto-spin (frame-count independent)
  // Resting auto-spin direction: +1 advances the frame index, which (for our renders) moves the
  // ring LEFT→RIGHT on screen. Independent of `reverse` (that only controls drag-follows-finger).
  // After a flick, the spin follows the flick direction until it settles back to this.
  var AUTO_SPIN_DIR = 1;
  var DECAY_TAU_SEC = 4; // flick momentum time-constant while ABOVE the baseline speed
  var EASE_TAU_SEC = 0.8; // gentler constant used to ease UP to the baseline from below
  // Constant friction added on top of the exponential decay, in ROTATIONS per second² (so it
  // scales with the frame count): 0.044 turns/s² = 8 frames/s² at 180 frames. Without it the
  // exponential tail crawls toward the baseline for ~20s; with it a flick melts into the
  // auto-spin in a few seconds — and it is clamped AT the baseline, never through it.
  var FRICTION_ROT_PER_SEC2 = 0.044;
  var MAX_FLICK_ROT_PER_SEC = 1.1; // brilliance caps a flick at ~1 rotation/sec (measured ~1.06)
  var FLICK_WINDOW_MS = 120; // measure flick speed over the last ~120ms (robust to finger-lift jitter)

  // Decoded footprint of a frame (RGBA), from its REAL pixel size — not the size we asked for.
  function decodedBytes(img) {
    if (!img) return 0;
    var w = img.naturalWidth || img.width || 0;
    var h = img.naturalHeight || img.height || 0;
    return w * h * 4;
  }

  // Widest rendition a device class may hold fully decoded: phones 640 (295 MB), tablets 820
  // (484 MB), big desktops 960 (663 MB < the 700 MB budget). Desktop retina tiles measure up to
  // ~1000 device px, so 960 is drawn ~1:1 there — 820 was upscaled ~1.2x and read as blur.
  function pickCapWidth() {
    var w = window.innerWidth || 1080;
    if (w < 768) return 640;
    if (w < 1200) return 820;
    // Chrome-family exposes deviceMemory (capped at 8 = "8 GB or more"); Safari/Firefox don't,
    // and stay on 960 rather than gambling ~840 MB of decoded frames on an unknown machine.
    return (navigator.deviceMemory || 0) >= 8 ? FULL_WIDTH : 960;
  }

  // float modulo into [0, n)
  function wrap(x, n) {
    return ((x % n) + n) % n;
  }
  // integer frame index from a float position
  function frameOf(pos, n) {
    return wrap(Math.round(pos), n);
  }

  // Cloudflare Image Transformations: serve AVIF to modern browsers, WebP to slightly
  // older ones, and a re-compressed JPEG to everything else — chosen automatically per
  // Cloudflare transforms are OFF by owner decision (2026-07-03): format=auto produced
  // byte-identical output to the raw webp originals while adding a second URL per frame
  // the browser couldn't dedupe against the raw/live-view fetches. Empty opts -> raw
  // origin URLs everywhere (cfTransform bypasses).
  var CF_IMAGE_BASE = '';
  function cfTransform(url, opts) {
    if (!opts) return url;
    if (url.indexOf('/cdn-cgi/image/') !== -1) return url; // already transformed
    var m = url.match(/^(https?:\/\/[^/]+)(\/.*)$/);
    if (!m) return url; // relative/odd URL — leave it alone
    return m[1] + '/cdn-cgi/image/' + opts + m[2];
  }

  function frameUrl(base, frame, pad, ext, prefix, opts) {
    var s = String(frame);
    while (s.length < pad) s = '0' + s;
    return cfTransform(base + (prefix || '') + s + '.' + ext, opts);
  }

  // Order frame indices so EVERY prefix is spread evenly around the ring. Repeatedly split the
  // largest remaining angular gap: at the mobile 16/32/64/128 stops, adjacent loaded frames
  // differ by at most one source-frame interval. This is important for a 180-frame corpus;
  // integer bit-reversal leaves badly uneven gaps when the frame count is not a power of two.
  function buildLoadOrder(n) {
    if (n <= 0) return [];
    var order = [0];
    // Unwrapped [start, end] arcs make the seam (last frame → frame 0) an ordinary gap.
    var gaps = [{ start: 0, end: n }];
    while (order.length < n) {
      var largest = 0;
      for (var i = 1; i < gaps.length; i++) {
        if (gaps[i].end - gaps[i].start > gaps[largest].end - gaps[largest].start) largest = i;
      }
      var gap = gaps[largest];
      var mid = gap.start + Math.floor((gap.end - gap.start) / 2);
      order.push(mid % n);
      gaps.splice(
        largest,
        1,
        { start: gap.start, end: mid },
        { start: mid, end: gap.end }
      );
    }
    return order;
  }

  // Slow only when the browser actually tells us so (Chromium/Android). Unknown (iOS,
  // Firefox) → treat as not-slow: the spin is an opt-in tab, so a full load is fine.
  function isSlowConnection() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var et = c.effectiveType || '';
    return et === 'slow-2g' || et === '2g' || et === '3g';
  }

  // GUARANTEED DECODE: onload only means the bytes arrived — the first drawImage of an
  // undecoded frame decodes it synchronously on the main thread (a visible hitch mid-spin).
  // img.decode() moves that off the critical path and tells us when the bitmap is really
  // ready, so a frame only counts as loaded once it can be drawn for free. Browsers without
  // decode() (or a decode that rejects) fall back to marking it on onload, as before.
  // We deliberately do NOT use fetch()+createImageBitmap: the images get cached by Cloudflare
  // from the plain <img> poster (a non-CORS request), so a later CORS fetch is blocked.
  function whenDecoded(img, cb) {
    if (img && typeof img.decode === 'function') {
      var p;
      try {
        p = img.decode();
      } catch (e) {
        cb(img);
        return;
      }
      if (p && typeof p.then === 'function') {
        p.then(
          function () { cb(img); },
          function () { cb(img); } // decode rejected (detached/odd codec) — still usable
        );
        return;
      }
    }
    cb(img);
  }

  // Try candidate URLs in order until one loads (per-frame fallback): a sized frame falls back
  // to a larger size, then to the native frame, so a single missing rendition never breaks the
  // spin. cb(img) on first success, cb(null) if all fail.
  function loadFrameChain(urls, cb, priority) {
    var i = 0;
    (function tryNext() {
      if (i >= urls.length) {
        cb(null);
        return;
      }
      var img = new Image();
      img.decoding = 'async';
      // Resource priority: an on-screen spin's stage-1 frames are 'high' so they aren't starved
      // behind the document/video on slow links (a bare new Image() defaults to a low priority
      // that just trickles in on 3G). Ignored by browsers without fetchPriority support.
      if (priority) { try { img.fetchPriority = priority; } catch (e) {} }
      img.onload = function () {
        whenDecoded(img, cb); // only report the frame once its bitmap is ready to draw
      };
      img.onerror = function () {
        i++;
        tryNext();
      };
      img.src = urls[i];
    })();
  }

  var SpinViewer = class extends HTMLElement {
    connectedCallback() {
      if (this._init) {
        // Same instance reconnected (e.g. Dawn moves the node rather than recreating it):
        // revive from the disconnectedCallback teardown instead of re-running first-time setup.
        if (this._dead) this._revive();
        return;
      }
      this._init = true;
      this._dead = false;

      var cfgEl = this.querySelector('.spin-360__config');
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }

      // Two frame counts: the sized (v2) set and the native fallback set. Which one applies is
      // decided per RING by the probe and stored on the cache (cache.frames) — never on `this`.
      this.nativeFrames = this.config.frames || NATIVE_FRAMES;
      this.sizedFrames = this.config.sizedFrames || SIZED_FRAMES;
      this.sizes =
        this.config.sizedWidths && this.config.sizedWidths.length ? this.config.sizedWidths : SIZES;
      this.ext = this.config.ext || 'webp';
      this.pad = this.config.pad || 5;
      this.framePrefix = this.config.framePrefix || '';
      this.spinVersion = this.config.spinVersion || 'v2'; // sized-frame path segment (cache-bust on re-render)
      this.reverse = !!this.config.reverse;
      this.sensitivity = this.config.dragSensitivity || 1;

      // The product spin remains functional when the OS has Reduced Motion enabled. It is a
      // slow, user-visible product view rather than a decorative transition; the separate
      // thumbnail hand hint still respects Reduced Motion and stays static.
      this.autoSpin = !!this.config.autoSpin;

      this.root = this.querySelector('.spin-360');
      this.canvas = this.querySelector('.spin-360__canvas');
      this.ctx = this.canvas.getContext('2d');
      this._paintedOk = false; // draw memo, see _draw
      // Chrome can drop a 2D canvas's bitmap (context loss under GPU pressure); on restore the
      // canvas is blank, so forget the memo and repaint the current frame.
      var selfCtx = this;
      this.canvas.addEventListener('contextrestored', function () {
        selfCtx._paintedOk = false;
        selfCtx._draw();
      });

      this.pos = 0; // float position in frames
      this.velocity = 0; // frames per SECOND (time-based)
      this.spinDir = AUTO_SPIN_DIR; // steady auto-spin direction (left→right; follows the last flick after that)
      this.dragging = false;
      this.interacted = false;
      this.visible = false;
      this.rafId = null;

      // Per-variant frame caches
      this.caches = {};
      this.activeVariantId = String(this.config.activeVariantId);
      this.current = this._cacheFor(this.activeVariantId);

      this._setupCanvas();
      this._bindPointer();
      this._observeVisibility();
      this._observeResize();
      this._observePageVisibility();
      this._subscribeVariant();
      // Per-variant visibility: hide the slide + thumbnail unless the active variant
      // has a spin. (Liquid sets the initial state; this re-asserts it on (re)connect,
      // including after the diamond customizer's AJAX section swap recreates this element.)
      this._applyVariantVisibility(this.activeVariantId);
      // The spin is rendered first + active server-side (no JS reorder → no load glitch).
      // If it's the active hero, start loading immediately so it paints the ring at once.
      this._startIfActive();
      this._listenCarat();
      this._syncCarat();
    }

    disconnectedCallback() {
      // Fully stop. The diamond customizer replaces the gallery via innerHTML, detaching this node;
      // without this teardown the rAF loop and in-flight frame loads keep running/decoding in the
      // background. Mark dead so the load callbacks + _loadRange/ladder bail and schedule no more
      // fetches, cancel the rAF, drop observers/listeners, and release decoded frames for GC.
      this._dead = true;
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._onWinResize) window.removeEventListener('resize', this._onWinResize);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._unsubscribe) this._unsubscribe();
      if (this._onCarat) document.removeEventListener('bc:carat-change', this._onCarat);
      // Drop the draw memo's frame reference so its decoded bitmap can be released too.
      this._paintedOk = false;
      this._paintedImg = null;
      // Null out decoded frames so a detached node's bitmaps don't stay retained, and reset each
      // cache's load state so a reconnect (Dawn reusing the node) reloads cleanly via _startLoad.
      for (var id in this.caches) {
        var c = this.caches[id];
        if (!c) continue;
        if (c.images) for (var i = 0; i < c.images.length; i++) c.images[i] = null;
        c.images = null;
        c.loaded = null;
        c.loadedCount = 0;
        c.residentBytes = 0;
        c.started = false;
        c.coarseDone = false;
        c.spinReady = false;
        c.frames = 0;
        c.fpsRetries = 0;
        c.rungs = null;
        c.rungIndex = null;
        c._chainFor = null;
      }
    }

    // Re-arm an instance that was torn down in disconnectedCallback and then reconnected.
    _revive() {
      this._dead = false;
      this.rafId = null;
      this._reported = false;
      this._observeVisibility();
      this._observeResize();
      this._observePageVisibility();
      this._subscribeVariant();
      this._applyVariantVisibility(this.activeVariantId);
      this._startIfActive();
      this._listenCarat();
      this._syncCarat();
    }

    // Caches are keyed by variant AND render folder: the same variant at another carat is a
    // different ring (…/ct-2/ vs …/ct-4/), so it gets its own frames.
    _cacheFor(variantId) {
      variantId = String(variantId);
      var raw = (this.config.variants && this.config.variants[variantId]) || null;
      var base = raw && window.BCCaratRenders ? window.BCCaratRenders.swap(raw) : raw;
      var key = variantId + '|' + (base || '');
      if (this.caches[key]) return this.caches[key];
      var cache = {
        variantId: variantId,
        base: base,
        frames: 0, // 180 (sized/v2) or 360 (native) — settled by the probe, see _beginLoad
        images: null, // allocated in _beginLoad once the frame count is known
        loaded: null,
        loadedCount: 0,
        coarseDone: false, // revealed (canvas visible, drag enabled)
        spinReady: false, // auto-spin allowed — set with coarseDone (first rung) or a gate stop
        fpsRetries: 0,
        started: false,
      };
      this.caches[key] = cache;
      return cache;
    }

    // Frame count of the ring currently on screen. Falls back to the sized count before the
    // probe settles (nothing is drawn then — it only keeps `pos` wrapping in a sane range).
    _frames() {
      var c = this.current;
      return (c && c.frames) || this.sizedFrames;
    }

    /* ---------- canvas sizing ---------- */
    _setupCanvas() {
      this._resizeCanvas();
    }

    _resizeCanvas() {
      // Same DPR cap as _pickWidth: backing store beyond 2× costs memory/fill-rate for pixels
      // no frame rendition can supply anyway (the widest v2 rendition is 960).
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = this.root.getBoundingClientRect();
      if (!rect.width) return;
      this._rectW = rect.width; // cached so dragMove doesn't measure layout each move (INP)
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this._paintedOk = false; // assigning width/height wipes the bitmap — repaint for sure
      this._draw();
    }

    _observeResize() {
      var self = this;
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(function () {
          self._resizeCanvas();
        });
        this._ro.observe(this.root);
      } else {
        this._onWinResize = function () {
          self._resizeCanvas();
        };
        window.addEventListener('resize', this._onWinResize);
      }
    }

    /* ---------- pause when the tab is hidden (save CPU/battery) ---------- */
    _observePageVisibility() {
      var self = this;
      this._onVisibility = function () {
        if (document.hidden) {
          if (self.rafId) {
            cancelAnimationFrame(self.rafId);
            self.rafId = null;
          }
        } else if (self.visible) {
          self._loop();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    /* ---------- lazy visibility ---------- */
    _observeVisibility() {
      var self = this;
      if (!('IntersectionObserver' in window)) {
        this.visible = true;
        this._startLoad();
        this._loop();
        return;
      }
      this._io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.visible = entry.isIntersecting;
            if (entry.isIntersecting) {
              self._startLoad();
              self._onVisibleResume();
              self._loop();
            }
          });
        },
        { threshold: 0.1 }
      );
      this._io.observe(this);
    }

    /* ---------- loading (Option A: pre-uploaded sized WebP, probe-gated) ---------- */

    // Smallest pre-uploaded width >= the displayed size (× DPR, capped at 2). Save-Data drops
    // DPR. Never undersizes below the display, so it's visually identical — it only stops
    // paying for pixels the screen can't show (and shrinks decode + retained-bitmap memory,
    // which scale with pixel AREA).
    _pickWidth() {
      var rect = this.root.getBoundingClientRect();
      var cssW = rect.width || this._rectW || window.innerWidth || 1080;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
      var target = c.saveData ? cssW : cssW * dpr;
      // Cap the spin's frame width so the WHOLE v2 ring (180 frames) fits the memory budget
      // (see maxResident) and drag is pop-in-free: phones ≤640 → 180 × 640² × 4 ≈ 295 MB of the
      // 340 MB phone budget; desktop ≤820 → ≈ 484 MB of the 700 MB budget. Both fit, so the
      // comfort gate can never stop a v2 ring short of 180. The eager poster still renders at
      // full res; this only bounds the per-frame decode/RAM cost.
      var capW = pickCapWidth();
      var sizes = this.sizes || SIZES;
      var pick = sizes[sizes.length - 1];
      for (var i = 0; i < sizes.length; i++) { if (sizes[i] >= target) { pick = sizes[i]; break; } }
      // Display outruns every rendition and the device can afford it → the source frames.
      if (capW >= FULL_WIDTH && target > sizes[sizes.length - 1]) return FULL_WIDTH;
      return Math.min(pick, capW);
    }

    // URL of a pre-uploaded sized frame: {base}360/{version}/{width}/frame_{NNNNN}.{ext}
    // (derived from framePrefix '360/frame_' by inserting version + width after the directory).
    _frameUrlSized(base, frame1, width) {
      if (width >= FULL_WIDTH) {
        // Full quality is not a generated tier: it maps straight onto the source frames
        // (360-set: sized k → native 2k-1; 180-set: 1:1), so it exists for every ring and
        // costs no extra storage. The ring's native count is probed before this path is used.
        var step = Math.round(nativeFramesFor(base, this.nativeFrames) / this.sizedFrames) || 1;
        return this._frameUrlNative(base, (frame1 - 1) * step + 1);
      }
      var s = String(frame1);
      while (s.length < this.pad) s = '0' + s;
      var fp = this.framePrefix || '360/frame_';
      var slash = fp.lastIndexOf('/');
      var dir = slash >= 0 ? fp.slice(0, slash + 1) : ''; // '360/'
      var file = slash >= 0 ? fp.slice(slash + 1) : fp; // 'frame_'
      return base + dir + this.spinVersion + '/' + width + '/' + file + s + '.' + this.ext;
    }

    // Native frame via the current Cloudflare transform — EXACTLY today's URL. Used as the
    // fallback and for any ring without sized assets (zero regression).
    _frameUrlNative(base, frame1) {
      return frameUrl(base, frame1, this.pad, this.ext, this.framePrefix, CF_IMAGE_BASE);
    }

    // Native frame index at the SAME angle as sized frame f1. v2 keeps the odd source frames
    // (1, 3, 5 … 359), so sized k → native 2k-1; identity in native mode.
    _nativeFrameFor(cache, f1) {
      var nat = nativeFramesFor(cache.base, this.nativeFrames);
      if (!cache.sized || !cache.frames || cache.frames >= nat) return f1;
      return (f1 - 1) * Math.round(nat / cache.frames) + 1;
    }

    // Run fn once this ring's native frame count is known (cached after the first probe).
    _withNativeCount(cache, fn) {
      var self = this;
      probeNativeFrames(cache.base, this._frameUrlNative(cache.base, NATIVE_PROBE_FRAME), this.nativeFrames, function () {
        if (self._dead || self.current !== cache) return;
        fn();
      });
    }

    _startLoad() {
      var cache = this.current;
      if (!cache.base) return;
      if (cache.started) {
        // Returning to a variant whose load was interrupted by an earlier switch (#3): resume it
        // instead of silently no-op'ing on the `started` guard (the old behavior → permanent stall).
        if (cache.resumable) { cache.resumable = false; this._resume(cache); }
        return;
      }
      this.root.classList.add('is-loading');
      // Desktop: the spin tile is on screen at first paint, so its first start would fetch the
      // probe + rung 1 at 'high' priority (and the rest of the ladder) inside the page's load
      // window. Defer that FIRST start to window `load` + idle. The poster (frame 1, the same
      // pixels) and the loading ring stay up meanwhile; once started, the ladder, frames and
      // speed are exactly as before. Mobile (< 750px) is unchanged.
      if (document.readyState !== 'complete' && this._isDesktop()) {
        this._deferStartToLoad();
        return;
      }
      cache.started = true;
      this._probeAndLoad(cache);
    }

    _isDesktop() {
      try { return !!(window.matchMedia && window.matchMedia('(min-width: 750px)').matches); } catch (e) { return false; }
    }

    // Schedule one _startLoad() for after window `load` + requestIdleCallback (timeout 1s).
    // Re-runs against whatever variant is current by then; repeat calls share one schedule.
    _deferStartToLoad() {
      if (this._startDeferred) return;
      this._startDeferred = true;
      var self = this;
      var fire = function () {
        self._startDeferred = false;
        if (self._dead) return;
        self._startLoad();
      };
      var afterLoad = function () {
        if ('requestIdleCallback' in window) window.requestIdleCallback(fire, { timeout: 1000 });
        else setTimeout(fire, 0);
      };
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
    }

    // Resume a variant whose ladder was abandoned by switching away mid-load (#3). Picks up from
    // cache.rungIndex, or re-runs the probe if the switch happened before the ladder was even built.
    // Already-loaded frames are skipped by _loadRange and re-fetches hit the HTTP cache → no double
    // fetch, no extra bytes. Caches stopped on purpose (comfort gate) are left as-is.
    _resume(cache) {
      if (!cache || !cache.started || !cache.base) return;
      if (cache.gateStopped) return; // stopped deliberately by the comfort gate — respect it
      if (cache.frames && cache.loadedCount >= cache.frames) return; // nothing left to load
      cache.pausedForVisibility = false;
      if (!cache.images || cache.rungs == null) { this._probeAndLoad(cache); return; } // probe never finished
      if (cache.rungIndex == null || cache.rungIndex >= cache.rungs.length) return; // ladder already done
      this._pumpLadder(cache);
    }

    // Probe frame 1 down the width chain: the picked width first, then each SMALLER sized
    // rendition (a ring generated before a new tier existed must land on its best available
    // size, not fall all the way to native), then native (today's behavior). The successful
    // probe IS frame 1, so the walk costs nothing extra on rings that have the picked size.
    // The probe decides the SCHEME and width, and those decide frame count + memory — so
    // nothing is allocated until it settles (see _beginLoad / _loadLadder).
    _probeAndLoad(cache) {
      var self = this;
      var picked = this._pickWidth();
      var sizes = this.sizes || SIZES;
      var widths = [];
      if (picked >= FULL_WIDTH) widths.push(FULL_WIDTH); // probe = the source frame 1 itself
      for (var i = sizes.length - 1; i >= 0; i--) if (sizes[i] <= picked) widths.push(sizes[i]);
      if (!widths.length) widths.push(sizes[0]);
      var wi = 0;
      var tryWidth = function () {
        if (self._dead || self.current !== cache) return;
        if (wi >= widths.length) {
          // No sized set at all → native mode; its frame count (180 or 360) decides the ring.
          self._withNativeCount(cache, function () { self._beginLoad(cache, false, null); });
          return;
        }
        var width = widths[wi];
        var img = new Image();
        img.decoding = 'async';
        // On-screen → fetch the probe (frame 1) at high priority so the ring appears fast on slow
        // links; warming off-screen → low, so it doesn't compete with the page's own load.
        try { img.fetchPriority = self.visible ? 'high' : 'low'; } catch (e) {}
        img.onload = function () {
          if (self._dead || self.current !== cache) return;
          whenDecoded(img, function () {
            if (self._dead || self.current !== cache) return;
            cache.width = width;
            self._beginLoad(cache, true, img);
          });
        };
        img.onerror = function () {
          wi++;
          tryWidth();
        };
        img.src = self._frameUrlSized(cache.base, 1, width);
      };
      // Full quality reads the source frames directly, so the ring's native count must be known
      // before the first URL is built; every sized width is count-independent.
      if (picked >= FULL_WIDTH) this._withNativeCount(cache, tryWidth);
      else tryWidth();
    }

    // Allocate the ring now that the probe has settled which scheme (and therefore how many
    // frames) this variant uses, then start the ladder. `probeImg` is frame 1, already decoded.
    _beginLoad(cache, sized, probeImg) {
      var n = sized ? this.sizedFrames : nativeFramesFor(cache.base, this.nativeFrames);
      cache.sized = sized;
      cache.frames = n;
      cache.images = new Array(n);
      cache.loaded = new Array(n).fill(false);
      cache.loadedCount = 0; // reset (also makes a resume re-probe safe — see _resume / #3)
      cache.residentBytes = 0;
      cache.droppedForMemory = 0;
      cache._order = buildLoadOrder(n); // order[0] === 0 → frame 1, the probe
      cache.rungIndex = null;
      cache.fpsRetries = 0;
      if (probeImg) {
        cache.images[0] = probeImg;
        cache.loaded[0] = true;
        cache.loadedCount = 1;
        cache.residentBytes = decodedBytes(probeImg);
        this._scheduleDraw();
      }
      this._loadLadder(cache);
    }

    // Load frames rung by rung: a small first rung (16 phones / 32 desktop) reveals and spins, then
    // the ladder doubles toward the complete set. The largest-gap order keeps every stop uniform all
    // round. After each rung, climb only while the device stays comfortable; otherwise stop and stay.
    _loadLadder(cache) {
      var self = this;
      var n = cache.frames;
      var width = cache.width;
      // Resident-frame cap, computed now that the probe has settled cache.sized. Base it on the
      // ACTUAL worst-case resident frame size (#1): native mode (probe failed) loads the ~1080px
      // originals, so the smaller picked width would undercount and keep far too many frames resident
      // (blowing the memory budget). We LOAD up to maxResident evenly-spread frames and KEEP them all
      // decoded (no eviction) so drag is pop-in-free; only the current variant is held resident (see
      // _evictCaches). A frame is effectiveWidth² × 4 bytes decoded → this is the decoded-byte ceiling.
      // ASSERTION (v2): 180 × 640² × 4 ≈ 295 MB < 340 MB (phone) and 180 × 820² × 4 ≈ 484 MB
      // < 700 MB (desktop) — with _pickWidth's 640/820 caps the whole sized ring always fits,
      // so maxResident >= 180 and the gate can never stop a v2 ring below the full set. Native
      // mode (1080² per frame) still caps out early, exactly as before.
      var effectiveWidth = cache.sized ? width : 1080;
      var memBudget = (window.innerWidth || 1080) < 768 ? 340 * 1024 * 1024 : 700 * 1024 * 1024;
      // Full-quality mode is only ever picked when deviceMemory reports >= 8, so it carries its
      // own budget: 180 × 1080² × 4 ≈ 838 MB — the whole ring still loads, the gate stays idle.
      if (cache.sized && width >= FULL_WIDTH) memBudget = 960 * 1024 * 1024;
      cache.memBudget = memBudget;
      // Projected decoded cost of a frame that is NOT resident yet. Frames that ARE resident are
      // counted at their REAL size (cache.residentBytes, see _retain) — a hole in the sized set
      // falls back to the ~1080px native frame (4.7 MB, not 1.6 MB), and a partially uploaded v2
      // set could otherwise pull 180 of those in under a 640-px budget and blow ~800 MB on a phone.
      cache.bytesPerFrame = effectiveWidth * effectiveWidth * 4;
      cache.maxResident = Math.min(
        n,
        Math.max(MIN_RESIDENT, Math.floor(memBudget / cache.bytesPerFrame))
      );
      // frames currently being fetched — keyed by frame index, cleared as each fetch settles, so the
      // rung loader never fetches the same frame twice.
      cache.loading = cache.loading || {};

      // Per-frame candidate chain. Sized rings fall back only to SAME-or-SMALLER renditions (never a
      // LARGER size — that would exceed the memory budget maxResident was computed against), then to
      // the native frame as a last resort if every sized rendition is missing.
      cache._chainFor = function (frame0) {
        var f1 = frame0 + 1;
        if (!cache.sized) return [self._frameUrlNative(cache.base, f1)];
        var sizes = self.sizes || SIZES;
        var urls = [self._frameUrlSized(cache.base, f1, width)];
        for (var i = sizes.length - 1; i >= 0; i--) {
          if (sizes[i] < width) urls.push(self._frameUrlSized(cache.base, f1, sizes[i]));
        }
        // Last resort for a hole in the sized set: the native frame at the SAME angle —
        // sized frame k is native frame 2k-1 (v2 keeps the odd source frames). At FULL width
        // that URL is already first in the chain, so don't repeat it.
        var nat = self._frameUrlNative(cache.base, self._nativeFrameFor(cache, f1));
        if (urls.indexOf(nat) === -1) urls.push(nat);
        return urls;
      };

      // Cumulative rungs, clamped to n, last == n.
      var targets = (window.innerWidth || 1080) < 768 ? RUNG_TARGETS_MOBILE : RUNG_TARGETS_DESKTOP;
      var rungs = [];
      for (var r = 0; r < targets.length; r++) {
        var v = Math.min(targets[r], n);
        if (rungs.indexOf(v) === -1) rungs.push(v);
      }
      if (rungs[rungs.length - 1] !== n) rungs.push(n);
      cache.rungs = rungs;
      if (cache.rungIndex == null) cache.rungIndex = 0;
      this._pumpLadder(cache);
    }

    // Load order[start..end) with bounded concurrency; calls done() when the range is fully
    // attempted. Skips frames already loaded or in flight (probe / decode-ahead / prior rung).
    _loadRange(cache, start, end, done, priority) {
      var self = this;
      var order = cache._order;
      var idx = start,
        inflight = 0,
        finished = false;
      var conc = Math.min(CONCURRENCY, Math.max(1, end - start));
      function pump() {
        if (self._dead || self.current !== cache) return; // detached, or variant switched away
        while (inflight < conc && idx < end) {
          var oi = idx++;
          var f0 = order[oi];
          if (cache.loaded[f0] || cache.loading[f0]) continue;
          inflight++;
          cache.loading[f0] = true;
          (function (frame0) {
            loadFrameChain(cache._chainFor(frame0), function (img) {
              if (self._dead) return; // detached mid-flight — cache.loading may already be nulled
              inflight--;
              cache.loading[frame0] = false;
              if (self.current !== cache) return;
              if (img && self._retain(cache, frame0, img)) self._scheduleDraw();
              if (idx < end) pump();
              else if (inflight === 0 && !finished) {
                finished = true;
                done();
              }
            }, priority);
          })(f0);
        }
        if (idx >= end && inflight === 0 && !finished) {
          finished = true;
          done();
        }
      }
      pump();
    }

    // Keep a decoded frame — unless it would push the variant over its decoded-byte budget, in
    // which case it is dropped and the hole is drawn from its nearest neighbour (_nearestLoaded).
    // Only oversize fallbacks can trip this (a sized frame is what the budget was sized for);
    // the coarse set (MIN_RESIDENT) is always kept so a ring never ends up too sparse to spin.
    _retain(cache, frame0, img) {
      var bytes = decodedBytes(img);
      if (cache.loadedCount >= MIN_RESIDENT && cache.memBudget &&
          cache.residentBytes + bytes > cache.memBudget) {
        cache.droppedForMemory = (cache.droppedForMemory || 0) + 1;
        return false;
      }
      cache.images[frame0] = img;
      cache.loaded[frame0] = true;
      cache.loadedCount++;
      cache.residentBytes += bytes;
      return true;
    }

    // Reveal the ring (swap the poster/loader for the live canvas). Only ever called while the
    // tile is on-screen, so the transition is seen, not wasted. Happens at the first rung; the
    // later rungs refine that same canvas in place while it spins.
    _reveal() {
      this.root.classList.remove('is-loading');
      this.root.classList.add('is-ready');
      if (!this.dragging && Math.abs(this.velocity) < 0.001) this.velocity = this._baseline();
      this._loop();
    }

    // Auto-spin may run. Normally already true from the first rung (see _pumpLadder); this also
    // covers a ladder that stops early — a stopped ring should spin with what it has, not freeze.
    _spinReady(cache) {
      if (cache.spinReady) return;
      cache.spinReady = true;
      if (this.visible && cache.coarseDone) {
        if (!this.dragging && Math.abs(this.velocity) < 0.001) this.velocity = this._baseline();
        this._loop();
      }
    }

    // Load the rung at cache.rungIndex, then decide whether to climb. Resumable: a warm
    // preload (tile off-screen) loads rung 1 and pauses here; _onVisibleResume continues it.
    _pumpLadder(cache) {
      var self = this;
      if (self._dead || self.current !== cache) return;
      var rungs = cache.rungs;
      if (cache.rungIndex >= rungs.length) { self._spinReady(cache); self._reportTelemetry(cache); return; }
      var start = cache.rungIndex === 0 ? 0 : rungs[cache.rungIndex - 1];
      var end = rungs[cache.rungIndex];
      // Rung 1 of an on-screen spin is what the shopper is waiting on → fetch it high priority so
      // it isn't starved on slow links; later rungs stay 'auto', warm preloads (off-screen) 'low'.
      var priority = self.visible ? (cache.rungIndex === 0 ? 'high' : 'auto') : 'low';
      // Belt-and-braces: never FETCH a rung that is already over the resident cap (a re-armed
      // gate must not walk into the rung the memory check refused).
      if (!self._memoryOk(cache, end)) {
        cache.gateStopped = true;
        cache.gateReason = 'mem';
        self._spinReady(cache);
        self._reportTelemetry(cache);
        return;
      }
      self._loadRange(cache, start, end, function () {
        if (self.current !== cache) return;
        // First rung in → show, spin and allow drag right away with the evenly spread frames
        // loaded so far; _nearestLoaded fills the gaps until later rungs refine them. A warm
        // preload defers the reveal to _onVisibleResume so it happens where it's seen.
        if (!cache.coarseDone) {
          cache.coarseDone = true;
          cache.spinReady = true;
          if (self.visible) self._reveal();
        }
        cache.rungIndex++;
        if (cache.rungIndex >= rungs.length) { // fully loaded → auto-spin may start
          self._spinReady(cache);
          self._reportTelemetry(cache);
          return;
        }
        // Climb only when on-screen (warm preload stops after rung 1) AND comfortable.
        if (!self.visible) { cache.pausedForVisibility = true; return; } // warm-stop → resume on view
        self._climbOrWait(cache);
      }, priority);
    }

    // Decide whether to load the next rung. Memory is a hard, PREDICTIVE stop (#2 — decide on the
    // NEXT rung's projected resident bytes BEFORE fetching it). A failed FPS check is NOT: the
    // spin may simply have been sampled during a scroll or another script's long task, so wait
    // ~3s and re-test, up to FPS_RETRIES times, before giving up.
    _climbOrWait(cache) {
      var self = this;
      var nextEnd = cache.rungs[cache.rungIndex];
      if (!this._memoryOk(cache, nextEnd)) {
        cache.gateStopped = true; // load-and-keep: the resident set is now fixed & capped
        cache.gateReason = 'mem'; // a hard bound — never re-armed (see _onVisibleResume)
        this._spinReady(cache);
        this._reportTelemetry(cache);
        return;
      }
      if (this._fpsOk()) {
        cache.fpsRetries = 0;
        setTimeout(function () { self._pumpLadder(cache); }, 0); // healthy → next rung
        return;
      }
      if (cache.fpsRetries < FPS_RETRIES) {
        cache.fpsRetries++;
        setTimeout(function () {
          if (self._dead || self.current !== cache) return;
          if (!self.visible) { cache.pausedForVisibility = true; return; }
          self._climbOrWait(cache);
        }, FPS_RETRY_MS);
        return;
      }
      cache.gateStopped = true; // still struggling after the re-tests — stop here and stay
      cache.gateReason = 'fps'; // soft — one more chance next time the tile is re-shown
      this._spinReady(cache);
      this._reportTelemetry(cache);
    }

    // Called when the tile scrolls into view: reveal a warmed ring instantly (frames already
    // loaded) and resume any ladder that paused after the warm rung-1 preload.
    _onVisibleResume() {
      var cache = this.current;
      if (!cache) return;
      if (cache.coarseDone && !this.root.classList.contains('is-ready')) this._reveal();
      // Re-arm the comfort gate once per resume: whatever made the device stutter (a scroll, a
      // heavy script, a background tab) is usually over by the time the shopper comes back.
      if (cache.gateStopped && cache.gateReason !== 'mem' && cache.rungs && cache.rungIndex != null &&
          cache.rungIndex < cache.rungs.length) {
        cache.gateStopped = false;
        cache.fpsRetries = 0;
        cache.pausedForVisibility = true;
      }
      if (cache.pausedForVisibility) {
        cache.pausedForVisibility = false;
        this._pumpLadder(cache);
      }
    }

    // Warm up stage 1 (16 frames on mobile) after the page is ready — even though the
    // spin is usually the 2nd gallery tile — so the ring is already spinning the moment it's
    // shown, with no cold start. Off-screen the ladder loads ONLY stage 1 then pauses (the rest
    // waits until the tile is actually viewed). Skipped on save-data / slow links.
    _warmStart() {
      if (this._warmed || !this.current) return;
      if (this.current.started || !this.current.base) return; // already loading / no spin for this variant
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
      if (c.saveData || /^(slow-2g|2g|3g)$/.test(c.effectiveType || '')) return;
      this._warmed = true;
      this._startLoad(); // visible is false → loads stage 1, then pauses (warm)
    }

    // Climb only while the device stays comfortable. Primary signal = sustained frame rate
    // (the only one reliable on iOS, where deviceMemory/NetInfo don't exist). Backstop = a
    // decoded-memory estimate, scaled so it never trips a device that copes today (desktop is
    // FPS-governed → reaches the full 360 as before). The ladder never loads MORE than today,
    // so this can only stop EARLIER on weak devices — no regression is possible.
    // PREDICTIVE frame-cap (#1/#2): stop climbing once the NEXT rung would exceed the resident cap
    // (the memory budget). We load up to maxResident evenly-spread frames and KEEP them all (no
    // eviction), so this is the hard memory bound — width is fixed per cache, so it's also a
    // decoded-bytes ceiling. Loading stops BEFORE fetching the over-budget rung. For v2 this never
    // trips: 180 frames at 640/820 fit both budgets (see the assertion in _loadLadder).
    _memoryOk(cache, projected) {
      if (!cache.bytesPerFrame || !cache.memBudget) return true; // ladder not built yet
      if (projected <= MIN_RESIDENT) return true; // the coarse set always loads
      // What is resident is counted at its REAL decoded size (native fallbacks for holes in a
      // sized set are ~3× a sized frame); what is still to come at the scheme's nominal size.
      var pending = Math.max(0, projected - cache.loadedCount);
      return cache.residentBytes + pending * cache.bytesPerFrame <= cache.memBudget;
    }

    // Sustained frame rate — the only weak-device signal that works on iOS (no deviceMemory /
    // NetInfo there). A single failure is not conclusive (a scroll or a foreign long task tanks
    // it for a moment), so the caller re-tests instead of freezing the ladder — see _climbOrWait.
    _fpsOk() {
      if (!this.autoSpin || !this._fps) return true; // no measurement yet → don't hold the ladder
      // A reading only means something while the loop is actually running. Idle (before reveal,
      // during a drag, after a variant switch) the number is frozen at whatever it last was, so
      // treat anything older than a second as "unknown" rather than as evidence of a slow device.
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      if (!this._fpsAt || now - this._fpsAt > FPS_STALE_MS) return true;
      return this._fps >= FPS_OK;
    }

    // Fire-and-forget, sampled, no-PII field data so we can tune the ladder/gate with real
    // devices during rollout. No-op if GA4 (gtag) isn't present.
    _reportTelemetry(cache) {
      if (this._reported) return;
      this._reported = true;
      // 2026-09-08 — an UNSAMPLED, payload-free dataLayer push for the ledger
      // relay (assets/bc-relay-shared.js → Meta `spin_360`, audience building).
      // The GA4 gtag call below stays at its 10% sample: it carries the
      // performance metrics and GA4 volume is the reason it is sampled.
      try { (window.dataLayer = window.dataLayer || []).push({ event: 'spin_360' }); } catch (e) {}
      try {
        if (Math.random() > 0.1) return; // 10% sample
        if (typeof window.gtag !== 'function') return;
        window.gtag('event', 'spin_360', {
          settled_rung: cache.loadedCount,
          dropped_for_memory: cache.droppedForMemory || 0,
          resident_mb: Math.round((cache.residentBytes || 0) / 1048576),
          version: cache.sized ? this.spinVersion || 'v2' : 'native',
          frames: cache.frames || 0,
          frame_width: cache.width || 0,
          fps: Math.round(this._fps || 0),
          sized: !!cache.sized,
          dpr: window.devicePixelRatio || 1,
          viewport_w: window.innerWidth || 0
        });
      } catch (e) {}
    }

    /* ---------- drawing ---------- */
    _nearestLoaded(cache, idx) {
      if (!cache.loaded) return null; // frames not built yet (probe still resolving)
      if (cache.loaded[idx]) return cache.images[idx];
      var n = cache.frames || cache.loaded.length;
      for (var d = 1; d < n; d++) {
        var a = wrap(idx - d, n);
        if (cache.loaded[a]) return cache.images[a];
        var b = wrap(idx + d, n);
        if (cache.loaded[b]) return cache.images[b];
      }
      return null;
    }

    _draw() {
      if (!this.ctx || !this.canvas.width) return;
      var idx = frameOf(this.pos, this._frames());
      var img = this._nearestLoaded(this.current, idx);
      var cw = this.canvas.width;
      var ch = this.canvas.height;
      // Memo: the loop ticks every display frame (60/120 Hz) but the rounded frame index moves
      // only ~15×/s, so most ticks would repaint the identical image. Skip when the same image
      // (or the same blank) is already on a canvas of the same size. A refined/other frame is a
      // different Image object, and resize/context-restore/teardown reset the memo.
      if (this._paintedOk && img === this._paintedImg && cw === this._paintedW && ch === this._paintedH) return;
      this.ctx.clearRect(0, 0, cw, ch);
      this._paintedOk = true;
      this._paintedImg = img;
      this._paintedW = cw;
      this._paintedH = ch;
      if (!img) return;
      var iw = img.naturalWidth || img.width; // Image OR ImageBitmap
      var ih = img.naturalHeight || img.height;
      var scale = Math.min(cw / iw, ch / ih); // contain
      var w = iw * scale;
      var h = ih * scale;
      this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }

    // Coalesce draws to at most one per frame (drag fires many moves; don't redraw each).
    _scheduleDraw() {
      if (this._drawPending) return;
      this._drawPending = true;
      var self = this;
      requestAnimationFrame(function () {
        self._drawPending = false;
        self._draw();
      });
    }

    /* ---------- animation loop (momentum that melts into a steady auto-spin) ---------- */
    _baseline() {
      // steady auto-spin speed in frames PER SECOND (one turn per AUTO_PERIOD_SEC — 15 fps at
      // 180 frames, 30 fps at 360), in the direction of the last flick; 0 when auto-spin is off
      // or the first rung hasn't landed yet.
      if (!this.autoSpin || !this.current.spinReady) return 0;
      return (this._frames() / AUTO_PERIOD_SEC) * this.spinDir;
    }

    _shouldAnimate() {
      if (this._dead || !this.visible || this.dragging) return false;
      if (this.autoSpin && this.current.spinReady) return true; // always keeps spinning
      return Math.abs(this.velocity) > 0.5; // residual momentum when auto-spin is configured off
    }

    _loop() {
      if (this.rafId || !this._shouldAnimate()) return;
      var self = this;
      self._lastFrameT = null;
      var tick = function (now) {
        self.rafId = null;
        if (self._lastFrameT == null) self._lastFrameT = now;
        var dt = (now - self._lastFrameT) / 1000; // seconds since last frame
        self._lastFrameT = now;
        if (dt > 0.05) dt = 0.05; // clamp big gaps (tab switch) so it doesn't jump
        if (dt > 0) { var inst = 1 / dt; self._fps = self._fps ? self._fps * 0.9 + inst * 0.1 : inst; self._fpsAt = now; }
        var n = self._frames();
        var baseline = self._baseline();
        // Velocity (frames/sec) always heads for `baseline`, never past it — the glide MELTS into
        // the auto-spin with no pause and no re-acceleration. Two regimes, both time-based (so
        // identical on 60Hz and 120Hz):
        //   above baseline → exponential decay (DECAY_TAU_SEC) PLUS constant friction, clamped AT
        //                    the baseline so it can't undershoot and swing back up;
        //   below baseline → a gentler ease-in (EASE_TAU_SEC) up to the baseline.
        var v = self.velocity;
        if (Math.abs(v) > Math.abs(baseline)) {
          v += (baseline - v) * (1 - Math.exp(-dt / DECAY_TAU_SEC));
          var friction = FRICTION_ROT_PER_SEC2 * n * dt; // frames/s² × s = frames/s
          if (v > baseline) v = Math.max(baseline, v - friction);
          else if (v < baseline) v = Math.min(baseline, v + friction);
        } else {
          v += (baseline - v) * (1 - Math.exp(-dt / EASE_TAU_SEC));
        }
        if (Math.abs(v - baseline) < 0.05) v = baseline;
        self.velocity = v;
        self.pos = wrap(self.pos + self.velocity * dt, n); // frames = fps × seconds
        self._draw();
        if (self._shouldAnimate()) self.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    }

    /* ---------- pointer interaction ---------- */
    _bindPointer() {
      var self = this;
      var startX = 0;
      var startPos = 0;

      // shared drag logic, fed by either mouse (Pointer events) or touch (Touch events)
      function dragStart(clientX, ts) {
        self.dragging = true;
        self.interacted = true;
        self.velocity = 0;
        if (self.rafId) { cancelAnimationFrame(self.rafId); self.rafId = null; } // nothing spins while held
        startX = clientX;
        startPos = self.pos;
        self._hist = [{ t: ts, raw: self.pos }]; // raw (unwrapped) samples for flick smoothing
        self.root.classList.add('is-dragging', 'is-interacted');
      }

      function dragMove(clientX, ts) {
        if (!self.dragging) return;
        var n = self._frames();
        var w = self._rectW || self.root.getBoundingClientRect().width; // cached, no per-move layout
        var framesPerPixel = (n / w) * self.sensitivity; // full width = one turn (any frame count)
        var dir = self.reverse ? 1 : -1;
        var raw = startPos + (clientX - startX) * framesPerPixel * dir;
        self.pos = wrap(raw, n);
        self._hist.push({ t: ts, raw: raw });
        while (self._hist.length > 2 && ts - self._hist[0].t > FLICK_WINDOW_MS) self._hist.shift();
        self._scheduleDraw();
      }

      function fling(ts) { // deliberate release → momentum
        if (!self.dragging) return;
        self.dragging = false;
        self.root.classList.remove('is-dragging');
        var hist = self._hist || [];
        var velFps = 0;
        if (hist.length >= 2) {
          var newest = hist[hist.length - 1];
          var oldest = hist[0];
          var secs = (newest.t - oldest.t) / 1000;
          if (secs > 0) velFps = (newest.raw - oldest.raw) / secs; // raw → no wrap ambiguity
        }
        self.velocity = velFps;
        var max = MAX_FLICK_ROT_PER_SEC * self._frames(); // cap ~1 rotation/sec like brilliance
        if (self.velocity > max) self.velocity = max;
        if (self.velocity < -max) self.velocity = -max;
        if (Math.abs(self.velocity) > 1) self.spinDir = self.velocity > 0 ? 1 : -1; // keep flicked direction
        self._loop();
      }

      function stop() { // interrupted (not a deliberate release) → NO momentum
        if (!self.dragging) return;
        self.dragging = false;
        self.root.classList.remove('is-dragging');
        self.velocity = self._baseline(); // resume gentle auto-spin
        self._loop();
      }

      // ----- MOUSE: Pointer events (desktop) -----
      this.canvas.addEventListener('pointerdown', function (e) {
        // Drag only once the ring is revealed (mobile: 16 frames; desktop sized: 90) — before
        // that the canvas is still behind the poster.
        if (e.pointerType !== 'mouse' || !self.current.base || !self.current.coarseDone) return; // touch handled below
        e.preventDefault();
        dragStart(e.clientX, e.timeStamp);
        try { self.canvas.setPointerCapture(e.pointerId); } catch (err) {}
      });
      this.canvas.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        dragMove(e.clientX, e.timeStamp);
      });
      this.canvas.addEventListener('pointerup', function (e) {
        if (e.pointerType !== 'mouse') return;
        fling(e.timeStamp);
        try { self.canvas.releasePointerCapture(e.pointerId); } catch (err) {}
      });
      this.canvas.addEventListener('pointercancel', function (e) {
        if (e.pointerType !== 'mouse') return;
        stop();
      });

      // ----- TOUCH: Touch events with a directional lock (mobile) -----
      // Horizontal → we claim it (preventDefault) so the browser can't scroll or cancel the
      // drag mid-gesture; vertical → we stay out of the way so the page scrolls normally.
      var tStartX = 0, tStartY = 0, tLock = null; // null | 'h' | 'v'
      this.canvas.addEventListener(
        'touchstart',
        function (e) {
          if (!self.current.base || !self.current.coarseDone || e.touches.length !== 1) { tLock = 'v'; return; }
          tStartX = e.touches[0].clientX;
          tStartY = e.touches[0].clientY;
          tLock = null;
        },
        { passive: true }
      );
      this.canvas.addEventListener(
        'touchmove',
        function (e) {
          if (e.touches.length !== 1) return;
          var t = e.touches[0];
          if (tLock === null) {
            var adx = Math.abs(t.clientX - tStartX);
            var ady = Math.abs(t.clientY - tStartY);
            if (adx < 6 && ady < 6) return; // wait until the direction is clear
            if (adx > ady) {
              tLock = 'h';
              dragStart(tStartX, e.timeStamp);
            } else {
              tLock = 'v'; // vertical → let the page scroll
              return;
            }
          }
          if (tLock === 'h') {
            if (e.cancelable) e.preventDefault(); // own the horizontal gesture
            dragMove(t.clientX, e.timeStamp);
          }
        },
        { passive: false }
      );
      this.canvas.addEventListener('touchend', function (e) {
        if (tLock === 'h') fling(e.timeStamp);
        tLock = null;
      });
      this.canvas.addEventListener('touchcancel', function () {
        if (tLock === 'h') stop();
        tLock = null;
      });

      // keyboard a11y
      this.canvas.setAttribute('tabindex', '0');
      this.canvas.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          self.interacted = true;
          self.root.classList.add('is-interacted');
          var dir = e.key === 'ArrowLeft' ? -1 : 1;
          var n = self._frames();
          self.pos = wrap(self.pos + dir * (n / 36), n); // 10° per press, whatever the frame count
          self._draw();
          e.preventDefault();
        }
      });
    }

    /* ---------- variant switching ---------- */
    _subscribeVariant() {
      var self = this;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (!variant) return;
        self._onVariantChange(variant.id);
      });
    }

    _hasSpinFor(variantId) {
      return !!(this.config.variants && this.config.variants[String(variantId)]);
    }

    _slideLi() {
      return this.closest('.product__media-item') || this.closest('li');
    }

    _thumbLi() {
      var gallery = this.closest('media-gallery');
      return gallery ? gallery.querySelector('.spin-360-thumb') : null;
    }

    // Show the spin slide + thumbnail only for variants that actually have a spin.
    _applyVariantVisibility(variantId) {
      var show = this._hasSpinFor(variantId);
      [this._slideLi(), this._thumbLi()].forEach(function (el) {
        if (!el) return;
        el.classList.toggle('spin-360-hidden', !show);
        if (show) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    }

    _onVariantChange(variantId) {
      this._applyVariantVisibility(variantId);
      if (this._hasSpinFor(variantId)) this.setVariant(variantId);
      // If the new variant has no spin, Dawn's own setActiveMedia has already moved the
      // gallery to that variant's featured image, so we just stay hidden.
      // The new variant has its own render folder — make sure it shows the chosen carat too.
      this._syncCarat();
    }

    /* ---------- carat (customizer weight) switching ---------- */
    _listenCarat() {
      if (!this._onCarat) this._onCarat = this._syncCarat.bind(this);
      document.removeEventListener('bc:carat-change', this._onCarat);
      document.addEventListener('bc:carat-change', this._onCarat);
    }

    // Check the chosen carat's folder for the active variant, then switch the ring if the
    // folder it should show differs from the one on screen. A missing folder resolves to
    // the original base, so the spin never breaks.
    _syncCarat() {
      var R = window.BCCaratRenders;
      if (R && R.syncStills) R.syncStills(); // gallery render stills follow the same carat/variant
      if (!R || !this.config || !this.config.variants) return;
      var raw = this.config.variants[this.activeVariantId];
      if (!raw) return;
      var self = this;
      R.probe(raw, function () {
        if (self._dead || !self.isConnected) return;
        var cur = self.config.variants[self.activeVariantId];
        if (cur && R.swap(cur) !== (self.current && self.current.base)) self._showVariant(self.activeVariantId);
      });
    }

    // Point the still poster + the gallery thumbnail (and its rocking hint) at the ring on
    // screen, so what shows before the canvas is ready matches the metal and carat.
    _syncStills() {
      var base = this.current && this.current.base;
      if (!base) return;
      var s = '1';
      while (s.length < this.pad) s = '0' + s;
      var src = base + (this.framePrefix || '') + s + '.' + this.ext;
      var poster = this.querySelector('.spin-360__poster');
      if (poster && poster.getAttribute('src') !== src) poster.setAttribute('src', src);
      var li = this._thumbLi();
      if (!li) return;
      var thumb = li.querySelector('.spin-360-thumb-img');
      if (thumb && thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
      var hint = li.querySelector('.spin-360-thumb-hint');
      if (hint && hint.getAttribute('data-spin-base') !== base) hint.setAttribute('data-spin-base', base);
    }

    // If the spin slide is the active hero (rendered first server-side), load now.
    _startIfActive() {
      var slide = this._slideLi();
      if (slide && slide.classList.contains('is-active') && this._hasSpinFor(this.activeVariantId)) {
        this.visible = true;
        this._resizeCanvas();
        this._startLoad();
        this._loop();
      }
    }

    setVariant(variantId) {
      variantId = String(variantId);
      if (variantId === this.activeVariantId) return;
      if (!this.config.variants || !this.config.variants[variantId]) return; // no spin for this variant
      // Karat-only switch (10k/14k/18k): the new variant resolves to the same frames folder
      // as the ring on screen, so keep spinning it instead of restarting the load (and
      // re-firing spin_360). Same resolution as _cacheFor. Any other change of folder
      // (metal, carat) still goes through _showVariant exactly as before.
      var raw = this.config.variants[variantId];
      var base = window.BCCaratRenders ? window.BCCaratRenders.swap(raw) : raw;
      if (this.current && this.current.base && base === this.current.base) {
        this.activeVariantId = variantId;
        return;
      }
      this._showVariant(variantId);
    }

    // Swap the ring on screen to variantId's frames (at the chosen carat). Also used for a
    // carat change on the SAME variant, which is why it has no same-variant guard.
    _showVariant(variantId) {
      var prevCache = this.current;
      // If we're leaving a variant whose ladder hadn't finished (and wasn't gate-stopped on purpose),
      // mark it resumable so coming back restarts/resumes it rather than stalling on `started` (#3).
      if (prevCache && prevCache.started && !prevCache.gateStopped &&
          !(prevCache.rungs && prevCache.rungIndex != null &&
            prevCache.rungIndex >= prevCache.rungs.length)) {
        prevCache.resumable = true;
      }
      this.activeVariantId = variantId;
      this.current = this._cacheFor(variantId);
      this.interacted = false;
      this.velocity = 0;
      this._reported = false;
      // The comfort-gate reading belongs to the ring that was spinning; a low sample from the
      // previous metal must not hold THIS ring's ladder below its reveal rung (the loop is idle
      // until reveal, so the stale number would never refresh and the gate would give up).
      this._fps = 0;
      this._fpsAt = 0;
      this.root.classList.toggle('is-ready', this.current.coarseDone);
      this._syncStills();
      this._draw();
      if (this.visible) {
        this._startLoad();
        this._loop();
      }
      this._evictCaches(); // free memory from variants we're no longer showing (#3)
    }

    // Keep only the current variant (+ the one we just left, to avoid a flash on quick
    // back-and-forth); release the rest's decoded frames so clicking through metals can't
    // grow memory without bound (the classic multi-variant blow-up).
    _evictCaches() {
      // Keep ONLY the current variant resident — each variant's frame set is held fully decoded
      // (no per-frame eviction), so two at once would ~double the footprint toward the crash zone.
      // Switching back reloads from the HTTP cache (fast); the poster <img> shows instantly meanwhile.
      for (var id in this.caches) {
        if (id === this.activeVariantId) continue;
        var c = this.caches[id];
        if (c === this.current) continue;
        if (c.images) for (var i = 0; i < c.images.length; i++) c.images[i] = null;
        c.images = null;
        c.loaded = null;
        c._chainFor = null;
        c._order = null;
        delete this.caches[id];
      }
    }
  };

  customElements.define('spin-360-viewer', SpinViewer);

  /* ---------- 360 thumbnail micro-interaction ----------
     The gallery 360 thumbnail shows the spin's first frame with a small hand. Drive the
     hand along a circular ARCH (not a straight line) and rock the underlying ring a few
     frames in sync, so the thumb visibly "comes alive" and reads as drag-to-spin. A single
     rAF loop drives the hand transform AND the frame swap so they never drift apart. */
  function thumbPad(n, w) {
    var s = String(n);
    while (s.length < w) s = '0' + s;
    return s;
  }
  // Width the MAIN viewer will pick on this page (mirror of _pickWidth, measured on the
  // real <spin-360-viewer> element when present). The rocking preload fetches frames at
  // THIS size from the pre-uploaded ladder so every wiggle frame warms the exact cache
  // entry the viewer loads later — a shared download instead of a throwaway thumbnail.
  function sharedViewerWidth() {
    var el = document.querySelector('spin-360-viewer');
    var rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    var cssW = (rect && rect.width) || window.innerWidth || 1080;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
    var target = c.saveData ? cssW : cssW * dpr;
    var capW = pickCapWidth(); // same caps as _pickWidth
    var sizes = spinPageConfig().sizedWidths || SIZES;
    var pick = sizes[sizes.length - 1];
    for (var i = 0; i < sizes.length; i++) { if (sizes[i] >= target) { pick = sizes[i]; break; } }
    return Math.min(pick, capW);
  }
  // The page's spin config (version / sized frame count / widths), read once off the viewer's own
  // JSON block so the thumb warms EXACTLY the URLs the viewer will ask for. Defaults to v2.
  var _pageCfg = null;
  function spinPageConfig() {
    if (_pageCfg) return _pageCfg;
    _pageCfg = {};
    try {
      var el = document.querySelector('spin-360-viewer .spin-360__config');
      if (el) _pageCfg = JSON.parse(el.textContent) || {};
    } catch (e) {}
    return _pageCfg;
  }
  function thumbVersion() { return spinPageConfig().spinVersion || 'v2'; }
  function thumbSizedFrames() { return spinPageConfig().sizedFrames || SIZED_FRAMES; }
  function thumbNativeFrames() { return spinPageConfig().frames || NATIVE_FRAMES; }
  // Pre-uploaded sized frame ({base}360/v2/{width}/frame_NNNNN.ext) at the viewer's own
  // width — no Cloudflare transform (owner decision 2026-07-03).
  function thumbFrameUrl(base, n, w, ext, prefix, width) {
    var fp = prefix || '360/frame_';
    var slash = fp.lastIndexOf('/');
    var dir = slash >= 0 ? fp.slice(0, slash + 1) : '';
    var file = slash >= 0 ? fp.slice(slash + 1) : fp;
    return base + dir + thumbVersion() + '/' + (width || sharedViewerWidth()) + '/' + file + thumbPad(n, w) + '.' + ext;
  }
  // Same walk as the viewer's probe: shared width, then each smaller sized rendition, then raw —
  // so the warm-up hits the URLs the viewer will actually settle on for rings without every tier.
  function thumbFrameChain(base, n, w, ext, prefix) {
    var sizes = spinPageConfig().sizedWidths || SIZES;
    var shared = sharedViewerWidth();
    if (shared >= FULL_WIDTH) return [thumbFrameUrlRaw(base, n, w, ext, prefix)]; // full mode rocks the originals
    var urls = [];
    for (var i = sizes.length - 1; i >= 0; i--) if (sizes[i] <= shared) urls.push(thumbFrameUrl(base, n, w, ext, prefix, sizes[i]));
    if (!urls.length) urls.push(thumbFrameUrl(base, n, w, ext, prefix, sizes[0]));
    urls.push(thumbFrameUrlRaw(base, n, w, ext, prefix));
    return urls;
  }
  // Raw original — fallback for rings with no v2 set (frame still wiggles). `n` is a v2 index,
  // so map it back to the native frame at the SAME angle: v2 k → native 2k-1.
  function thumbFrameUrlRaw(base, n, w, ext, prefix) {
    var nat = nativeFramesFor(base, thumbNativeFrames());
    var sized = thumbSizedFrames();
    var step = sized && nat > sized ? Math.round(nat / sized) : 1;
    return base + (prefix || '') + thumbPad((n - 1) * step + 1, w) + '.' + ext;
  }
  function initSpinThumb(hint) {
    if (hint._spinThumbInit) return;
    hint._spinThumbInit = true;
    var hand = hint.querySelector('.spin-360-thumb-hand');
    if (!hand) return;
    var btn = hint.closest('.thumbnail') || hint.parentElement;
    var img = btn ? btn.querySelector('.spin-360-thumb-img') : null;
    var reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var SWIPE = 2400; // a slow, gentle swipe lasts this long (ms)
    var GAP = 650; // brief rest between the two swipes
    var REPEATS = 2; // play exactly twice each time the thumb scrolls into view, then stop
    // Rock offsets are in V2 frames (2° each), so both are half of the old native-frame values —
    // same visual swing (±30° / 2° steps), half the requests.
    var MAXOFF = isSlowConnection() ? 9 : 15; // v2 frames the ring rocks each side (±18°/±30°)
    var STEP = 1; // preload/snap every v2 frame (= 2° — the finest the ring has)
    var AMP = 30; // horizontal swing, % of the hand's own box (subtle)
    var ARCH = 12; // how high the hand lifts mid-swipe, %
    var TILT = 8; // wrist tilt, deg
    var PEAK_OP = 0.7; // hand opacity at the peak of a swipe (it's hidden at rest)

    hand.style.animation = 'none'; // take over from the CSS fallback

    // Spin source metadata (read from data-* attributes) used by the rocking-frame preload below.
    var base = hint.getAttribute('data-spin-base') || '';
    // Offsets are counted in V2 frames (the gallery's data-spin-frames is the NATIVE count and is
    // only used for the raw fallback, inside thumbFrameUrlRaw).
    var frames = thumbSizedFrames();
    var ext = hint.getAttribute('data-spin-ext') || 'webp';
    var padw = parseInt(hint.getAttribute('data-spin-pad'), 10) || 5;
    var prefix = hint.getAttribute('data-spin-prefix') || '';
    var loaded = {};
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
    var preloaded = false;
    // The viewer re-points data-spin-base when the metal or carat changes (_syncStills). Drop
    // the old ring's rocking frames so the thumb never rocks a different ring than it shows;
    // the next preload fetches the new ones.
    function syncBase() {
      var b = hint.getAttribute('data-spin-base') || '';
      if (b === base) return;
      base = b;
      loaded = {};
      preloaded = false;
      hideRock();
    }
    // Preload the rocking frames (every STEP, both sides), keeping only those that exist. Deferred:
    // runs only when the thumb is actually in view (see the IntersectionObserver below) and on idle,
    // so it never competes with the page's initial load. Skipped on Save-Data and reduced-motion.
    function preloadRockingFrames() {
      syncBase();
      if (preloaded || !(img && base) || conn.saveData) return;
      preloaded = true;
      var runBase = base;
      var run = function () {
        // Nearest offsets first (±1, ±2, …) so the rock has frames to draw as soon as possible,
        // and only THUMB_CONCURRENCY requests in flight at once — the old loop fired all ~30 at
        // once, on top of the viewer's own rung-1 burst, which is what leaned on the origin.
        var queue = [];
        for (var o = STEP; o <= MAXOFF; o += STEP) queue.push(o, -o);
        var active = 0;
        var next = function () {
          while (active < THUMB_CONCURRENCY && queue.length) {
            var off = queue.shift();
            var idx = (((off % frames) + frames) % frames) + 1; // frame 1 = base; wrap across the seam
            active++;
            (function (off, urls) {
              var pre = new Image();
              try { pre.fetchPriority = 'low'; } catch (e) {}
              var ui = 0;
              var settled = false;
              var settle = function () { if (settled) return; settled = true; active--; next(); };
              pre.onload = function () {
                var src = pre.src; // capture: a fallback retry mutates pre.src
                whenDecoded(pre, function () {
                  if (runBase === base) loaded[off] = src; // only offer a frame the rock can draw without a decode hitch
                  settle();
                });
              };
              pre.onerror = function () {
                // This rendition is missing for this ring — walk down the same chain the viewer uses.
                ui++;
                if (ui < urls.length) { pre.src = urls[ui]; return; }
                settle();
              };
              pre.src = urls[0];
            })(off, thumbFrameChain(base, idx, padw, ext, prefix));
          }
        };
        next();
      };
      // Wait for window `load` BEFORE idling: on a slow connection the rIC timeout fires
      // mid-load, and these ~30 frames then compete with the hero/LCP for bandwidth
      // (measured: they started at 0.5s and pushed PDP load to 33s). Until they arrive the
      // thumb's hand still animates over the base frame, so nothing breaks meanwhile.
      var go = function () {
        var start = function () {
          // Full mode rocks the source frames, whose count differs per ring (180 or 360).
          if (sharedViewerWidth() >= FULL_WIDTH) {
            var probeUrl = base + (prefix || '') + thumbPad(NATIVE_PROBE_FRAME, padw) + '.' + ext;
            probeNativeFrames(base, probeUrl, thumbNativeFrames(), run);
          } else run();
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 4000 });
        else setTimeout(start, 600);
      };
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    }

    // Rocking frames render on an OVERLAY clone, never by mutating the base <img>:
    // changing the src of a rendered image INVALIDATES the page's LCP candidate
    // (measured: 23 invalidations per play, pinning PDP LCP to ~26s whenever the
    // frames arrived near trace end). The base img stays byte-identical for its
    // whole life; the overlay sits exactly over it and shows only mid-rock.
    var rock = null;
    function rockImg() {
      if (rock || !img) return rock;
      var parent = img.parentElement;
      if (parent && getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      rock = img.cloneNode(false);
      rock.removeAttribute('loading');
      rock.removeAttribute('fetchpriority');
      rock.removeAttribute('alt');
      rock.setAttribute('aria-hidden', 'true');
      rock.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;display:none;pointer-events:none;';
      parent.insertBefore(rock, img.nextSibling);
      return rock;
    }
    function showRock(src) {
      var r = rockImg();
      if (!r) return;
      if (r.getAttribute('src') !== src) r.src = src;
      r.style.display = 'block';
    }
    function hideRock() {
      if (rock) rock.style.display = 'none';
    }
    function rest() {
      hand.style.transform = 'translate(0,0) rotate(0deg)'; // sits low (hint is bottom-anchored)
      hand.style.opacity = '0'; // hand is only visible while it's moving
      hideRock();
    }
    if (reduce) {
      // no motion to gate visibility on — show a faint static hand as the hint
      hand.style.transform = 'translate(0,0) rotate(0deg)';
      hand.style.opacity = '0.5';
      return;
    }

    var raf = null;
    var start = null;
    var lastOff = 0;
    var inView = false;
    var UNIT = SWIPE + GAP;
    function tick(ts) {
      if (start == null) start = ts;
      var el = ts - start;
      var idx = Math.floor(el / UNIT); // which of the two swipes we're in
      if (idx >= REPEATS) {
        // played REPEATS times — settle at rest and stop until the thumb re-enters view
        raf = null;
        lastOff = 0;
        rest();
        return;
      }
      var tc = el - idx * UNIT; // time within this swipe+gap unit
      var x = 0;
      var y = 0;
      var theta = 0;
      var rot = 0;
      var op = 0; // hidden unless mid-swipe
      if (tc < SWIPE) {
        var a = tc / SWIPE; // 0..1 across the single swipe
        var env = Math.sin(Math.PI * a); // eases the whole swipe in and out (0 at both ends)
        var swing = -Math.sin(a * 2 * Math.PI); // left → centre → right
        theta = env * swing; // horizontal drive (also drives the frame rock)
        x = theta * AMP;
        y = -ARCH * env * (1 - Math.abs(swing)); // lifts as it passes centre; low at edges & at rest
        rot = theta * TILT;
        op = env * PEAK_OP; // fade the hand in/out with the swipe — only visible while moving
      }
      hand.style.transform =
        'translate(' + x.toFixed(1) + '%,' + y.toFixed(1) + '%) rotate(' + rot.toFixed(1) + 'deg)';
      hand.style.opacity = op.toFixed(2);
      syncBase();
      if (img && base) {
        var off = Math.round((theta * MAXOFF) / STEP) * STEP; // ring follows the hand, snapped to STEP
        if (off !== lastOff) {
          if (off === 0) {
            hideRock(); // base img (untouched underneath) shows through
          } else if (loaded[off]) {
            showRock(loaded[off]);
          }
          lastOff = off;
        }
      }
      raf = requestAnimationFrame(tick);
    }
    // Play the two swipes from the start (called when the thumb scrolls into view).
    function playFromStart() {
      if (document.hidden) return;
      if (raf) cancelAnimationFrame(raf);
      start = null;
      raf = requestAnimationFrame(tick);
    }
    function halt() {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      lastOff = 0;
      rest();
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            inView = e.isIntersecting;
            if (inView) {
              preloadRockingFrames(); // gated: only prefetch once the thumb is actually visible
              playFromStart(); // restart the 2-swipe sequence on each entry
            } else halt();
          });
        },
        { threshold: 0.35 }
      );
      io.observe(hint);
    } else {
      inView = true;
      preloadRockingFrames();
      playFromStart();
    }
    // Pause off-screen; replay the sequence when the tab is shown again and the thumb is in view.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (raf) {
          cancelAnimationFrame(raf);
          raf = null;
        }
      } else if (inView) {
        playFromStart();
      }
    });
  }
  function initAllSpinThumbs() {
    var els = document.querySelectorAll('.spin-360-thumb-hint');
    for (var i = 0; i < els.length; i++) initSpinThumb(els[i]);
  }
  if (document.readyState !== 'loading') initAllSpinThumbs();
  else document.addEventListener('DOMContentLoaded', initAllSpinThumbs);

  // Warm stage 1 of any spin on the page AFTER first paint (on idle), so the ring is instantly
  // alive when its tile is shown — without competing with the page's critical load / LCP.
  function warmAllSpins() {
    var els = document.querySelectorAll('spin-360-viewer');
    for (var i = 0; i < els.length; i++) if (typeof els[i]._warmStart === 'function') els[i]._warmStart();
  }
  function scheduleWarm() {
    var run = function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(warmAllSpins, { timeout: 2500 });
      else setTimeout(warmAllSpins, 1200);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }
  scheduleWarm();
})();
